import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import AppChip from "../ui/AppChip";
import ServiceFilterAccordion from "./ServiceFilterAccordion";
import { getCitiesForCountry } from "../../data/locationTaxonomy";
import { filterProfiles } from "../../data/mockProfiles";
import {
  DISTANCE_OPTIONS,
  CATEGORY_OPTIONS,
  DEFAULT_PROFILE_FILTERS,
  DEFAULT_EXPANDED_SERVICE_SECTIONS,
  SERVICE_SECTIONS,
  normalizeProfileFilters,
} from "../../data/profileFilterOptions";
import { useLanguage } from "../../i18n";
import "./MapSearchSheet.css";

const SHEET_CLOSE_MS = 380;

const FILTER_TAB_IDS = ["region", "category", "services"];

function toggleInList(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export default function MapSearchSheet({
  open,
  onClose,
  filters,
  onApplyFilters,
  onResetFilters,
  browseCountry = "",
  profiles = [],
}) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("region");
  const [draftFilters, setDraftFilters] = useState(filters);
  const [expandedServiceSections, setExpandedServiceSections] = useState(
    () => ({ ...DEFAULT_EXPANDED_SERVICE_SECTIONS }),
  );
  const [isAnimatedIn, setIsAnimatedIn] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const requestCloseRef = useRef(null);

  const requestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setIsAnimatedIn(false);
    window.setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, SHEET_CLOSE_MS);
  }, [isClosing, onClose]);

  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (!open) return;

    setDraftFilters(normalizeProfileFilters(filtersRef.current));
    setExpandedServiceSections({ ...DEFAULT_EXPANDED_SERVICE_SECTIONS });
    setActiveTab("region");
    setIsClosing(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    document.documentElement.classList.add("search-sheet-open");
    document.body.style.overflow = "hidden";

    const enterFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setIsAnimatedIn(true);
      });
    });

    const onKey = (e) => {
      if (e.key === "Escape") requestCloseRef.current?.();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.cancelAnimationFrame(enterFrame);
      setIsAnimatedIn(false);
      document.documentElement.classList.remove("search-sheet-open");
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const previewCount = useMemo(
    () => filterProfiles(profiles, draftFilters).length,
    [profiles, draftFilters],
  );

  const setField = useCallback((key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleCity = useCallback((city) => {
    setDraftFilters((prev) => ({
      ...prev,
      cities: toggleInList(prev.cities || [], city),
    }));
  }, []);

  const toggleCategory = useCallback((category) => {
    setDraftFilters((prev) => ({
      ...prev,
      categories: toggleInList(prev.categories || [], category),
    }));
  }, []);

  const toggleService = useCallback((service) => {
    setDraftFilters((prev) => ({
      ...prev,
      services: toggleInList(prev.services || [], service),
    }));
  }, []);

  const toggleServiceSection = useCallback((sectionId) => {
    setExpandedServiceSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const [resetPulseKey, setResetPulseKey] = useState(0);
  const [resetPulsing, setResetPulsing] = useState(false);
  const resetPulseTimerRef = useRef(null);

  useEffect(() => () => {
    if (resetPulseTimerRef.current) clearTimeout(resetPulseTimerRef.current);
  }, []);

  const handleResetPointerDown = useCallback(() => {
    flushSync(() => {
      setResetPulseKey((key) => key + 1);
      setResetPulsing(true);
    });

    if (resetPulseTimerRef.current) clearTimeout(resetPulseTimerRef.current);
    resetPulseTimerRef.current = setTimeout(() => setResetPulsing(false), 560);

    setDraftFilters(normalizeProfileFilters(DEFAULT_PROFILE_FILTERS));
    setExpandedServiceSections({ ...DEFAULT_EXPANDED_SERVICE_SECTIONS });
  }, []);

  const handleApply = useCallback(() => {
    onApplyFilters?.(normalizeProfileFilters(draftFilters));
    requestClose();
  }, [draftFilters, onApplyFilters, requestClose]);

  if (!open && !isClosing) return null;

  const cities = getCitiesForCountry(browseCountry);

  const rootClassName = [
    "map-search-sheet-root",
    isAnimatedIn ? "map-search-sheet-root--open" : "",
    isClosing ? "map-search-sheet-root--closing" : "",
  ].filter(Boolean).join(" ");

  return createPortal(
    <div className={rootClassName} role="presentation">
      <button
        type="button"
        className="map-search-sheet-backdrop"
        onClick={requestClose}
        aria-label={t("filter.close")}
      />
      <div className="map-search-sheet" role="dialog" aria-modal="true" aria-label={t("filter.title")}>
        <div className="map-search-sheet__handle" aria-hidden />

        <header className="map-search-sheet__toolbar">
          <h2 className="map-search-sheet__title">{t("filter.title")}</h2>
          <button
            type="button"
            className={`map-search-sheet__reset${resetPulsing ? " map-search-sheet__reset--pulse" : ""}`}
            onPointerDown={handleResetPointerDown}
            onClick={(event) => event.preventDefault()}
          >
            {resetPulseKey > 0 ? (
              <span key={resetPulseKey} className="map-search-sheet__reset-ripple" aria-hidden />
            ) : null}
            {t("filter.reset")}
          </button>
        </header>

        <div className="map-search-sheet__tabs" role="tablist" aria-label={t("filter.title")}>
          {FILTER_TAB_IDS.map((tabId) => (
            <button
              key={tabId}
              type="button"
              role="tab"
              aria-selected={activeTab === tabId}
              className={`map-search-sheet__tab${activeTab === tabId ? " map-search-sheet__tab--active" : ""}`}
              onClick={() => setActiveTab(tabId)}
            >
              {t(`filter.${tabId}`)}
            </button>
          ))}
        </div>

        <div className="map-search-sheet__body map-search-sheet__body--tabs">
          {activeTab === "region" && (
            <section className="map-search-sheet__panel" role="tabpanel">
              <button
                type="button"
                className={`map-search-sheet__nearby-btn${draftFilters.nearby ? " map-search-sheet__nearby-btn--active" : ""}`}
                onClick={() => setField("nearby", !draftFilters.nearby)}
              >
                {t("filter.nearby", { km: draftFilters.nearbyKm ?? 15 })}
              </button>

              <div className="map-search-sheet__section">
                <h3>{t("filter.distance")}</h3>
                <div className="map-search-sheet__chips">
                  {DISTANCE_OPTIONS.map((km) => (
                    <AppChip
                      key={km}
                      label={`${km} km`}
                      selected={draftFilters.distanceKm === km}
                      onClick={() => {
                        setField("distanceKm", km);
                        setField("nearbyKm", km);
                      }}
                    />
                  ))}
                </div>
              </div>

              {cities.length > 0 ? (
                <div className="map-search-sheet__section">
                  <h3>{t("filter.city")}</h3>
                  <div className="map-search-sheet__chips">
                    <AppChip
                      label={t("filter.allCities")}
                      selected={!(draftFilters.cities || []).length}
                      onClick={() => setField("cities", [])}
                    />
                    {cities.map((city) => (
                      <AppChip
                        key={city}
                        label={city}
                        selected={(draftFilters.cities || []).includes(city)}
                        onClick={() => toggleCity(city)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          )}

          {activeTab === "category" && (
            <section className="map-search-sheet__panel" role="tabpanel">
              <div className="map-search-sheet__chips">
                {CATEGORY_OPTIONS.map((category) => (
                  <AppChip
                    key={category}
                    label={category}
                    selected={(draftFilters.categories || []).includes(category)}
                    onClick={() => toggleCategory(category)}
                  />
                ))}
              </div>
            </section>
          )}

          {activeTab === "services" && (
            <section className="map-search-sheet__panel" role="tabpanel">
              <ServiceFilterAccordion
                sections={SERVICE_SECTIONS}
                selected={draftFilters.services || []}
                onToggle={toggleService}
                expandedSections={expandedServiceSections}
                onToggleSection={toggleServiceSection}
              />
            </section>
          )}
        </div>

        <footer className="map-search-sheet__footer">
          <button type="button" className="map-search-sheet__apply btn-primary" onClick={handleApply}>
            {previewCount === 1
              ? t("filter.showProfile", { count: previewCount })
              : t("filter.showProfiles", { count: previewCount })}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
